import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	inject,
	input,
	OnInit,
	Output,
	signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ILabel } from '@app/pages/settings/interfaces/labels.interfaces';
import { FormControl } from '@angular/forms';
import { LabelBadgeComponent } from '@app/pages/settings/labels/label-item-row/label-badge/label-badge.component';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { LabelListMenuComponent } from '@app/pages/cmms/components/shared-form-group-controls/cmms-labels-section/label-list-menu/label-list-menu.component';
import { LabelsService } from '@app/shared/services/labels.service';
import { take } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { NgxTolgeeModule } from '@tolgee/ngx';

@Component({
	selector: 'cmms-labels-section',
	templateUrl: './cmms-labels-section.component.html',
	styleUrls: ['./cmms-labels-section.component.scss'],
	imports: [
		CommonModule,
		LabelBadgeComponent,
		MatIconModule,
		MatMenuModule,
		LabelListMenuComponent,
		MatButtonModule,
		NgxTolgeeModule,
	],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CmmsLabelsSectionComponent implements OnInit {
	private readonly labelsService = inject(LabelsService);

	labelsList = input<ILabel[]>([]);
	initSize = input(5);
	editable = input(true);

	@Output() labelsListChanged = new EventEmitter<{ labels: string[] }>();
	@Output() updatedLabelsList = new EventEmitter<{ labels: ILabel[] }>();
	@Output() controlReady = new EventEmitter<FormControl>();

	selectedLabels = signal<ILabel[]>([]);
	appliedLabels = signal<ILabel[]>([]);
	labels = this.labelsService.labels;

	labelsCollapsed = signal(true);
	formControl = new FormControl([]);
	size: number | undefined;

	ngOnInit() {
		this.size = this.initSize();
		this.selectedLabels.set(this.labelsList());
		this.setLabelPksToFormControl(this.labelsList());
		this.appliedLabels.set(this.selectedLabels());
		this.controlReady.emit(this.formControl);

		this.formControl.valueChanges.pipe(take(1)).subscribe(value => {
			this.selectedLabels.set(
				this.labels()
					.map(label => value.includes(label.pk) && label)
					.filter(Boolean)
			);
		});
	}

	onSelectedLabelsChange($event: { pks: string[]; labels: ILabel[] }) {
		this.formControl.setValue($event.pks);
		this.selectedLabels.set($event.labels);
	}

	toggleLabelsAmount() {
		this.size = this.labelsCollapsed() ? undefined : this.initSize();
		this.labelsCollapsed.set(!this.labelsCollapsed());
	}

	deleteLabel(label: ILabel) {
		const newLabelList = this.selectedLabels().filter(l => l.pk !== label.pk);
		this.selectedLabels.set(newLabelList);
		this.formControl.setValue(newLabelList.map(item => item.pk));
		this.labelsListChanged.emit({ labels: this.formControl.value });
		this.updatedLabelsList.emit({ labels: newLabelList });
	}

	menuClosedHandler() {
		if (this.appliedLabels() === this.selectedLabels()) {
			return;
		}
		this.labelsListChanged.emit({ labels: this.formControl.value });
		this.updatedLabelsList.emit({ labels: this.selectedLabels() });
		this.appliedLabels.set(this.selectedLabels());
	}

	private setLabelPksToFormControl(labels: ILabel[]) {
		this.formControl.setValue(labels.map(label => label.pk));
	}
}
